import {useEffect, useEffectEvent, useRef, useState} from 'react'

import type {PlayButtonAction} from '../../../../../../brand/components/PlayButton/PlayButton'
import type {HeroPaginationProps} from './types'

/**
 * Handles chapter navigation, playback-button actions, and progress animation.
 *
 * For video playback, subscribes directly to the shared playback source. The browser advances
 * the animation between updates without rendering. The button action comes from React props.
 *
 * Without video playback, the progress animation acts as a chapter timer and advances to
 * the next chapter when it finishes.
 */
export const useHeroPagination = ({
  currentChapterIndex,
  items,
  onChange,
  video,
  prefersReducedMotion,
}: Pick<HeroPaginationProps, 'currentChapterIndex' | 'items' | 'onChange' | 'video'> & {
  prefersReducedMotion: boolean
}) => {
  const [pausedOverride, setPausedOverride] = useState<boolean | null>(null)
  const [restartCount, setRestartCount] = useState(0)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [finishedChapter, setFinishedChapter] = useState<{index: number; duration: number | undefined} | null>(null)

  const viewportRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLLIElement>(null)
  const selectedButtonRef = useRef<HTMLButtonElement>(null)
  const progressRef = useRef<HTMLSpanElement>(null)
  const animationRef = useRef<Animation | null>(null)

  const playbackStateSubscription = video?.playbackStateSubscription
  const duration = items[currentChapterIndex]?.duration
  const isPaused = prefersReducedMotion || (video ? video.playButtonAction !== 'pause' : (pausedOverride ?? false))
  const isFinished = video ? video.playButtonAction === 'replay' : finishedChapter !== null
  const isControlled = playbackStateSubscription !== undefined
  const chapterStart = items.slice(0, currentChapterIndex).reduce((total, item) => total + item.duration, 0)
  const playButtonAction: PlayButtonAction =
    video?.playButtonAction ?? (isFinished ? 'replay' : isPaused ? 'play' : 'pause')

  useEffect(() => {
    const viewport = viewportRef.current
    const firstItem = firstItemRef.current
    if (!viewport || !firstItem) return

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries.at(-1)
        if (entry?.rootBounds) setHasScrolled(entry.boundingClientRect.left < entry.rootBounds.left)
      },
      {root: viewport, threshold: [0, 1]},
    )
    observer.observe(firstItem)
    return () => observer.disconnect()
  }, [items.length])

  if (
    finishedChapter &&
    (finishedChapter.index !== currentChapterIndex ||
      finishedChapter.duration !== duration ||
      currentChapterIndex !== items.length - 1)
  ) {
    setFinishedChapter(null)
  }

  const advanceChapter = useEffectEvent(() => {
    if (isPaused) return

    if (currentChapterIndex === items.length - 1) {
      setFinishedChapter({index: currentChapterIndex, duration})
    } else {
      onChange(currentChapterIndex + 1)
    }
  })

  useEffect(() => {
    const progressElement = progressRef.current
    if (!progressElement || !duration || duration <= 0) return

    const animation = progressElement.animate([{transform: 'scaleX(0)'}, {transform: 'scaleX(1)'}], {
      duration: duration * 1000,
      easing: 'linear',
      fill: 'forwards',
    })
    animation.pause()

    // Video events, not animation completion, select chapters when playback is controlled.
    if (!isControlled) animation.onfinish = advanceChapter
    animationRef.current = animation

    return () => {
      animation.onfinish = null
      animation.cancel()
      animationRef.current = null
    }
  }, [currentChapterIndex, duration, items.length, restartCount, isControlled])

  useEffect(() => {
    // Apply reported video positions; the browser animates between updates without React renders.
    const syncPlayback = () => {
      const playbackState = playbackStateSubscription?.getState()

      if (playbackState && duration && animationRef.current) {
        const elapsed = playbackState.currentTime - chapterStart
        animationRef.current.currentTime = Math.max(0, Math.min(elapsed, duration)) * 1000
      }

      if (!prefersReducedMotion && (playbackState?.isPlaying ?? !isPaused)) {
        animationRef.current?.play()
      } else {
        animationRef.current?.pause()
      }
    }

    // Chapter changes create a new animation, which needs the saved position before the next event.
    syncPlayback()

    return playbackStateSubscription?.subscribe(syncPlayback)
  }, [
    isPaused,
    playbackStateSubscription,
    chapterStart,
    currentChapterIndex,
    duration,
    items.length,
    restartCount,
    isControlled,
    prefersReducedMotion,
  ])

  useEffect(() => {
    const viewport = viewportRef.current
    const button = selectedButtonRef.current
    if (!viewport || !button) return

    const viewportBounds = viewport.getBoundingClientRect()
    const buttonBounds = button.getBoundingClientRect()
    const viewportStyle = getComputedStyle(viewport)
    const visibleLeft = viewportBounds.left + parseFloat(viewportStyle.scrollPaddingInlineStart)
    const visibleRight = viewportBounds.right - parseFloat(viewportStyle.scrollPaddingInlineEnd)
    const offset =
      buttonBounds.left < visibleLeft ? buttonBounds.left - visibleLeft : Math.max(0, buttonBounds.right - visibleRight)

    if (offset !== 0) {
      viewport.scrollBy({left: offset, behavior: prefersReducedMotion ? 'instant' : 'smooth'})
    }
  }, [currentChapterIndex, prefersReducedMotion])

  const selectChapter = (index: number) => {
    setFinishedChapter(null)
    setRestartCount(count => count + 1)
    onChange(index)
  }

  const handlePlaybackClick = () => {
    if (video) {
      video.onTogglePlay()
      return
    }

    if (isFinished) {
      selectChapter(0)
      setPausedOverride(false)
    } else {
      setPausedOverride(!isPaused)
    }
  }

  return {
    hasScrolled,
    firstItemRef,
    playButtonAction,
    viewportRef,
    selectedButtonRef,
    progressRef,
    selectChapter,
    handlePlaybackClick,
  }
}
